import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { Button } from './va-button';
import { ConfigurableUi } from '../../configurable-ui';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { VaIconModule } from '../../va-icon/icon.module';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';

@Component({
  selector: 'va-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (config of buttons; track config) {
      @if (!config.link) {
        @switch (config.type) {
          @case ('flat') {
            <button
              mat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              (click)="clicked(config)"
              >
              {{ config.title }}
            </button>
          }
          @case ('raised') {
            <button
              mat-flat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              (click)="clicked(config)"
              >
              {{ config.title }}
            </button>
          }
          @case ('icon') {
            @if (!config.classIcon) {
              <button
                mat-icon-button
                [attr.data-testid]="config.dataTestId"
                [glxyTooltip]="config.tooltip"
                [tooltipPositions]="config.tooltipPosition"
                [color]="config.color"
                [disabled]="config.disabled"
                [attr.data-pendo]="config.pendo"
                (click)="clicked(config)"
                >
                <mat-icon [style.color]="config.iconColor">{{ config.icon }}</mat-icon>
              </button>
            }
            @if (config.classIcon) {
              <button
                mat-icon-button
                [attr.data-testid]="config.dataTestId"
                [glxyTooltip]="config.tooltip"
                [tooltipPositions]="config.tooltipPosition"
                [color]="config.color"
                [disabled]="config.disabled"
                [attr.data-pendo]="config.pendo"
                (click)="clicked(config)"
                >
                <span [class]="config.classIcon" [style.color]="config.iconColor"></span>
              </button>
            }
          }
        }
      }
      @if (config.link && config.link.external) {
        @switch (config.type) {
          @case ('flat') {
            <a
              mat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [href]="config.link.url"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              [target]="config.link.target"
              >
              {{ config.title }}
            </a>
          }
          @case ('raised') {
            <a
              mat-flat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [href]="config.link.url"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              [target]="config.link.target"
              >
              {{ config.title }}
            </a>
          }
          @case ('icon') {
            <a
              mat-icon-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [disabled]="config.disabled"
              [href]="config.link.url"
              [attr.data-pendo]="config.pendo"
              [target]="config.link.target"
              (click)="clicked(config)"
              >
              @if (!config.classIcon) {
                <mat-icon [style.color]="config.iconColor">
                  {{ config.icon }}
                </mat-icon>
              }
              @if (config.classIcon) {
                <span [class]="config.classIcon" [style.color]="config.iconColor"></span>
              }
            </a>
          }
        }
      }
      @if (config.link && !config.link.external) {
        @switch (config.type) {
          @case ('flat') {
            <a
              mat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [routerLink]="config.link.url"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              [target]="config.link.target"
              >
              {{ config.title }}
            </a>
          }
          @case ('raised') {
            <a
              mat-flat-button
              [attr.data-testid]="config.dataTestId"
              [glxyTooltip]="config.tooltip"
              [tooltipPositions]="config.tooltipPosition"
              [color]="config.color"
              [routerLink]="config.link.url"
              [disabled]="config.disabled"
              [attr.data-pendo]="config.pendo"
              [target]="config.link.target"
              >
              {{ config.title }}
            </a>
          }
          @case ('icon') {
            @if (!config.classIcon) {
              <a
                mat-icon-button
                [attr.data-testid]="config.dataTestId"
                [glxyTooltip]="config.tooltip"
                [tooltipPositions]="config.tooltipPosition"
                [color]="config.color"
                [disabled]="config.disabled"
                [attr.data-pendo]="config.pendo"
                [routerLink]="config.link.url"
                [target]="config.link.target"
                (click)="clicked(config)"
                >
                @if (!config.classIcon) {
                  <mat-icon [style.color]="config.iconColor">{{ config.icon }}</mat-icon>
                }
                @if (config.classIcon) {
                  <span [class]="config.classIcon" [style.color]="config.iconColor"></span>
                }
              </a>
            }
          }
        }
      }
    }
    `,
  imports: [CommonModule, RouterModule, VaIconModule, MatIconModule, MatTooltipModule, MatButtonModule, GalaxyTooltip],
})
export class VaButtonComponent implements ConfigurableUi {
  @Input() config: Button | Button[];

  get buttons(): Button[] {
    if (Array.isArray(this.config)) {
      return this.config;
    } else {
      return [this.config];
    }
  }

  clicked(button: Button): void {
    if (button.onClick) {
      button.onClick();
    }
  }
}
